'use client';

/**
 * StoreVisitTracker — Client Component wrapper for the SSR store page.
 *
 * Sprint 138 (PR-E: Feature 1.B — Seller Welcome, ADR-058 §Feature 1
 * APROBADO por Founder 2026-09-25).
 *
 * Spec ref: docs/SPEC-NEGOCIO-LOGICA-VIVA.md §10.15.2 + INV-WELCOME-01..10.
 *
 * Purpose: when an authenticated comprador visits `/market/tienda/:storeSlug`
 * for the first time, this component fires the trigger (a) "visita tienda
 * por primera vez" by calling POST /api/welcome-message/seller/trigger
 * with { sellerId, triggerType: 'store_visit' }.
 *
 * The store page is SSR (server-rendered for SEO), so we can't fire the
 * trigger from the server (no userId in SSR session). This Client Component
 * is mounted as a child of the SSR page; on mount it:
 *   1. Checks if the visitor is authenticated (via useAuth)
 *   2. If yes, fires the trigger (best-effort, silent failure)
 *   3. If no (anonymous visitor), silent skip (no userId to send to)
 *
 * Idempotencia: the backend handles "1 mensaje por par (comprador, seller)"
 * via User.welcomeMessagesReceivedFromSellers array. If the comprador already
 * received a welcome from this seller (via any trigger), the backend returns
 * { sent: false, reason: 'already_sent' } silently. No special handling
 * needed here.
 *
 * UX: the trigger is invisible to the user. No loading state, no toasts,
 * no UI feedback. The welcome message arrives in the comprador's inbox
 * (DirectMessage TTL 48h) — the comprador discovers it organically.
 *
 * §6 Anti-Invención: thin client wrapper — no business logic. All
 * idempotencia + send logic is backend-side.
 *
 * Sprint 138 (PR-E: Feature 1.B — Seller Welcome).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/use-auth';
import { api } from '@/lib/api-client';

export interface StoreVisitTrackerProps {
  /**
   * The public store slug (from URL: `/market/tienda/[storeSlug]`).
   * The backend resolves storeSlug → seller.userId internally — we don't
   * pass private user IDs from the public SSR page (privacy).
   */
  storeSlug: string;
}

/**
 * Renders nothing — purely a side-effect trigger on mount.
 * Returns null so it doesn't affect the SSR layout.
 */
export function StoreVisitTracker({ storeSlug }: StoreVisitTrackerProps) {
  const { user, loading } = useAuth();
  const triggeredRef = useRef(false);

  useEffect(() => {
    // Skip if still loading auth state, or if user is not authenticated
    // (anonymous visitor — no userId to send the welcome to).
    if (loading || !user) return;
    // Defense-in-depth: don't fire twice in StrictMode double-render.
    if (triggeredRef.current) return;
    triggeredRef.current = true;

    // Best-effort trigger — silent failure. The backend returns 200 with
    // { sent: false, reason: 'already_sent' | 'no_active_welcome' | ... }
    // for all non-success cases, so no error toast is shown. If the network
    // request fails entirely, also silent (catch + ignore).
    api.welcomeMessage
      .triggerSeller({ storeSlug }, 'store_visit')
      .catch(() => {
        // Silent fail — the welcome message is best-effort. The comprador's
        // visita to the store must NOT be blocked by a welcome trigger failure.
      });
  }, [user, loading, storeSlug]);

  return null;
}

export default StoreVisitTracker;
